import { Button } from "../components/ui/button";
import { Link } from "../components/ui/link";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-12 px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        <div className="prose dark:prose-invert max-w-none space-y-8">
          <section>
            <p className="text-muted-foreground">Last updated: 15.12.2024</p>
            <p className="text-lg mt-4">
              PentAGI is an ethical security testing tool designed exclusively
              for lawful use and requires explicit authorization from the system
              owner before any testing activities can commence.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">
              Limitation of Liability
            </h2>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-6">
              <p className="font-medium text-destructive mb-4">
                Important Notice:
              </p>
              <ul className="list-disc list-inside space-y-4 ml-4">
                <li className="pl-2">
                  PentAGI is provided "as is" without any warranties of any
                  kind, either express or implied
                </li>
                <li className="pl-2">
                  The creators and developers disclaim all liability for any
                  unauthorized or unlawful use of the tool
                </li>
                <li className="pl-2">
                  Using PentAGI for unauthorized access or attacks is strictly
                  prohibited and may result in legal consequences
                </li>
                <li className="pl-2">
                  Users assume full responsibility and liability for all actions
                  performed using this tool
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Authorized Use</h2>
            <p className="mb-4">PentAGI may only be used for:</p>
            <ul className="list-disc list-inside space-y-4 mb-4 ml-4">
              <li className="pl-2">
                Authorized security testing with explicit written permission
              </li>
              <li className="pl-2">
                Testing of your own infrastructure where you have full authority
              </li>
              <li className="pl-2">
                Educational purposes in controlled environments
              </li>
              <li className="pl-2">
                Ethical hacking activities with documented authorization from
                the system owner
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">
              Data Processing and Privacy
            </h2>
            <div className="bg-muted rounded-lg p-6">
              <ul className="space-y-4">
                <li className="pl-2">
                  ✓ Local Processing: All core operations are executed within
                  your infrastructure
                </li>
                <li className="pl-2">
                  ✓ Data Control: You maintain full control over all data and
                  testing results
                </li>
                <li className="pl-2">
                  ✓ Isolation: Each test runs in an isolated Docker container
                  for enhanced security
                </li>
                <li className="pl-2">
                  ✓ LLM Integration: Interactions with OpenAI and Anthropic
                  services are configurable and controlled by the user
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">
              AI Service Providers
            </h2>
            <p className="mb-4">
              PentAGI integrates with Large Language Models (LLMs) through:
            </p>
            <ul className="list-disc list-inside space-y-4 mb-4 ml-4">
              <li className="pl-2">
                OpenAI and Anthropic services, as configured by the user
              </li>
              <li className="pl-2">
                Langfuse integration for monitoring and controlling data
                transmitted to LLM providers
              </li>
              <li className="pl-2">
                User-configurable privacy settings for AI service interactions
              </li>
            </ul>
            <p>
              Users are responsible for ensuring compliance with the respective
              terms of service and data handling policies of these providers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">External Services</h2>
            <p>
              When utilizing additional services (Google, Tavily, Traversaal)
              for information gathering, users must comply with their respective
              terms of service and privacy policies. Data transmission to these
              services is configurable and controlled by the user.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Security</h2>
            <p className="mb-4">
              While PentAGI implements robust security measures and isolation
              mechanisms, users must ensure:
            </p>
            <ul className="list-disc list-inside space-y-4 mb-4 ml-4">
              <li className="pl-2">
                Secure runtime environment implementation
              </li>
              <li className="pl-2">Proper access control and authentication</li>
              <li className="pl-2">Comprehensive activity monitoring</li>
              <li className="pl-2">
                Compliance with applicable laws and regulations
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Policy Updates</h2>
            <p>
              We reserve the right to modify this policy at any time. Users will
              be notified of any changes through the project repository.
              Continued use of PentAGI constitutes acceptance of the updated
              policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Contact</h2>
            <p>
              For security concerns and compliance inquiries:{" "}
              <a
                href="mailto:info@pentagi.com"
                className="text-primary hover:underline"
              >
                info@pentagi.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 flex justify-between items-center">
          <Button asChild variant="outline">
            <Link href="/">← Back to Home</Link>
          </Button>
          <Button asChild variant="default">
            <a
              href="https://github.com/vxcontrol/pentagi"
              target="_blank"
              rel="noopener noreferrer"
            >
              Project Repository →
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
